import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  OrganizationService,
  UpdateOrganizationData,
  AxiosErrorResponse,
} from "@/lib/api/services/organizationService";

export interface OrganizationState {
  isLoading: boolean;
  error: string | null;
  slugCheckResult: {
    available: boolean;
    message: string;
    suggestions?: string[];
  } | null;
}

const initialState: OrganizationState = {
  isLoading: false,
  error: null,
  slugCheckResult: null,
};

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export const getMyOrganizations = createAsyncThunk(
  "organization/getMyOrganizations",
  async (_, { rejectWithValue }) => {
    try {
      return await OrganizationService.getMyOrganizations();
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getOrganizationById = createAsyncThunk(
  "organization/getOrganizationById",
  async (organizationId: number, { rejectWithValue }) => {
    try {
      return await OrganizationService.getOrganizationById(organizationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getOrganizationBySlug = createAsyncThunk(
  "organization/getOrganizationBySlug",
  async (slug: string, { rejectWithValue }) => {
    try {
      return await OrganizationService.getOrganizationBySlug(slug);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const updateOrganization = createAsyncThunk(
  "organization/updateOrganization",
  async (
    {
      organizationId,
      orgData,
    }: {
      organizationId: number;
      orgData: UpdateOrganizationData;
    },
    { rejectWithValue }
  ) => {
    try {
      return await OrganizationService.updateOrganization(
        organizationId,
        orgData
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const checkSlugAvailability = createAsyncThunk(
  "organization/checkSlugAvailability",
  async (slug: string, { rejectWithValue }) => {
    try {
      return await OrganizationService.checkSlugAvailability(slug);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getOrganizationMembers = createAsyncThunk(
  "organization/getOrganizationMembers",
  async (organizationId: number, { rejectWithValue }) => {
    try {
      return await OrganizationService.getOrganizationMembers(organizationId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSlugCheck: (state) => {
      state.slugCheckResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMyOrganizations.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getMyOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getOrganizationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganizationById.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getOrganizationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getOrganizationBySlug.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganizationBySlug.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getOrganizationBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(checkSlugAvailability.pending, (state) => {
        state.slugCheckResult = null;
      })
      .addCase(checkSlugAvailability.fulfilled, (state, action) => {
        state.slugCheckResult = {
          available: action.payload.available,
          message: action.payload.message,
          suggestions: action.payload.suggestions,
        };
      })
      .addCase(checkSlugAvailability.rejected, (state) => {
        state.slugCheckResult = null;
      })
      .addCase(getOrganizationMembers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrganizationMembers.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getOrganizationMembers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSlugCheck } = organizationSlice.actions;

export default organizationSlice.reducer;
